import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router-dom";
import ProductsList from "../components/ProductsList";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faXmark,
  faSlidersH,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import "../styles/products-page.css";

const ApiURL = process.env.REACT_APP_API_URL;

const FilterLabel = ({ title, children }) => (
  <div className="fp-section">
    <div className="fp-section-label">
      <span className="fp-section-title">{title}</span>
    </div>
    <div className="fp-section-body">{children}</div>
  </div>
);

const FilterSection = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="fp-section">
      <button className="fp-section-header" onClick={() => setOpen((o) => !o)}>
        <span className="fp-section-title">{title}</span>
        <FontAwesomeIcon
          icon={open ? faChevronUp : faChevronDown}
          className={`fp-section-toggle${open ? " open" : ""}`}
        />
      </button>
      {open && <div className="fp-section-body">{children}</div>}
    </div>
  );
};

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [categoryTree, setCategoryTree] = useState([]);
  const [allBrands, setAllBrands] = useState([]);
  const [availableBrandIds, setAvailableBrandIds] = useState(new Set());
  const [availableTipos, setAvailableTipos] = useState([]);
  const [subCounts, setSubCounts] = useState({});
  const [brandCounts, setBrandCounts] = useState({});
  const [expandedCatId, setExpandedCatId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const subSlug = searchParams.get("sub") || "";
  const catSlug = searchParams.get("cat") || "";
  const brandParam = searchParams.get("brand") || "";
  const tipoParam = searchParams.get("tipo") || "";
  const searchTerm = searchParams.get("q") || "";
  const sortParam = searchParams.get("sort") || "";

  const rawPage = parseInt(searchParams.get("page") || "1", 10);
  const page = isNaN(rawPage) || rawPage < 1 ? 1 : rawPage;

  const [searchInput, setSearchInput] = useState(searchTerm);

  useEffect(() => {
    setSearchInput(searchTerm);
  }, [searchTerm]);

  const updateParams = useCallback(
    (updates) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (v) next.set(k, v);
            else next.delete(k);
          });
          next.delete("page");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput === searchTerm) return;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (searchInput) next.set("q", searchInput);
          else next.delete("q");
          next.delete("page");
          return next;
        },
        { replace: true },
      );
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput, searchTerm, setSearchParams]);

  const selSub = useMemo(() => {
    if (!subSlug || !categoryTree.length) return null;
    for (const cat of categoryTree) {
      const sub = cat.subcategories?.find((s) => s.slug === subSlug);
      if (sub) return sub;
    }
    return null;
  }, [subSlug, categoryTree]);

  const selCat = useMemo(() => {
    if (!categoryTree.length) return null;
    if (subSlug) {
      for (const cat of categoryTree) {
        if (cat.subcategories?.find((s) => s.slug === subSlug)) return cat;
      }
    }
    if (catSlug) return categoryTree.find((c) => c.slug === catSlug) || null;
    return null;
  }, [subSlug, catSlug, categoryTree]);

  const selBrands = useMemo(
    () => new Set(brandParam ? brandParam.split(",").filter(Boolean) : []),
    [brandParam],
  );

  const selTipos = useMemo(
    () => new Set(tipoParam ? tipoParam.split(",").filter(Boolean) : []),
    [tipoParam],
  );

  const brandKey = [...selBrands].sort().join(",");
  const tipoKey = [...selTipos].join(",");

  const setPage = useCallback(
    (p) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (p > 1) next.set("page", String(p));
          else next.delete("page");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Scroll lock robusto para mobile (iOS y Android)
  useEffect(() => {
    if (sidebarOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";
      return () => {
        const y = document.body.style.top;
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.style.overflow = "";
        if (y) window.scrollTo(0, parseInt(y || "0", 10) * -1);
      };
    }
  }, [sidebarOpen]);

  const fetchInitialData = useCallback(async () => {
    try {
      const [brandsRes, categoriesRes] = await Promise.all([
        axios.get(`${ApiURL}/brands`),
        axios.get(`${ApiURL}/categories`),
      ]);
      setAllBrands(
        [...brandsRes.data].sort((a, b) => a.name.localeCompare(b.name)),
      );
      setCategoryTree(categoriesRes.data);
    } catch (err) {
      console.error("Error fetching initial data:", err);
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    if (selCat) setExpandedCatId(selCat._id);
    else setExpandedCatId(null);
  }, [selCat]);

  const fetchAvailableFilters = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (catSlug) params.append("category", catSlug);
      if (subSlug) params.append("subcategory", subSlug);
      if (brandKey) params.append("brand", brandKey);
      if (tipoKey) params.append("tipo", tipoKey);
      const res = await axios.get(`${ApiURL}/categories/filters?${params}`);
      setAvailableBrandIds(new Set(res.data.availableBrands.map((b) => b._id)));
      setAvailableTipos(res.data.availableTipos || []);
      const sc = {};
      (res.data.subCounts || []).forEach(({ _id, count }) => {
        sc[_id] = count;
      });
      setSubCounts(sc);
      const bc = {};
      (res.data.availableBrands || []).forEach(({ _id, count }) => {
        bc[_id] = count;
      });
      setBrandCounts(bc);
    } catch (err) {
      console.error("Error fetching available filters:", err);
    }
  }, [subSlug, catSlug, brandKey, tipoKey]);

  useEffect(() => {
    fetchAvailableFilters();
  }, [fetchAvailableFilters]);

  const isMobile = () =>
    typeof window !== "undefined" && window.innerWidth <= 960;

  const toggleCat = (cat) => {
    setExpandedCatId((prev) => (prev === cat._id ? null : cat._id));
  };

  const selectSub = (cat, sub) => {
    if (selSub?._id === sub._id) {
      updateParams({ sub: "", cat: "", tipo: "" });
    } else {
      updateParams({ sub: sub.slug, cat: "", tipo: "" });
      setExpandedCatId(cat._id);
    }
    if (isMobile()) setSidebarOpen(false);
  };

  const toggleBrand = (brandId) => {
    const next = new Set(selBrands);
    next.has(brandId) ? next.delete(brandId) : next.add(brandId);
    updateParams({ brand: [...next].sort().join(",") });
    if (isMobile()) setSidebarOpen(false);
  };

  const toggleTipo = (tipo) => {
    const next = new Set(selTipos);
    next.has(tipo) ? next.delete(tipo) : next.add(tipo);
    updateParams({ tipo: [...next].join(",") });
    if (isMobile()) setSidebarOpen(false);
  };

  const clearAll = () => {
    setSearchParams({}, { replace: true });
  };

  const hasActiveFilters =
    selCat || selSub || selBrands.size > 0 || selTipos.size > 0 || searchTerm;
  const activeCount =
    (selCat ? 1 : 0) +
    (selSub ? 1 : 0) +
    selBrands.size +
    selTipos.size +
    (searchTerm ? 1 : 0);
  const hasTipos = selSub && availableTipos.length > 0;
  const selectedSubObjects = selSub ? [selSub] : [];
  const categoryFilter = subSlug || catSlug;
  const brandFilter = brandKey;
  const tipoFilter = tipoKey;

  return (
    <div className="products-page">
      <Helmet>
        <title>Productos | Cáceres Maquinarias</title>
        <meta
          name="description"
          content="Explorá el catálogo completo de productos STIHL. Motosierras, cortadoras de césped, motoguadañas, sopladores, hidrolavadoras y más. Concesionario oficial en Casilda."
        />
        <meta property="og:title" content="Productos | Cáceres Maquinarias" />
        <meta
          property="og:description"
          content="Explorá el catálogo completo de productos STIHL y Tromen. Motosierras, cortadoras de césped, motoguadañas, sopladores, hidrolavadoras y más."
        />
        <meta
          property="og:url"
          content="https://www.caceresmaquinarias.com.ar/productos"
        />
        <meta
          property="og:image"
          content="https://www.caceresmaquinarias.com.ar/Logo-CM.png"
        />
      </Helmet>

      <div className="fp-layout">
        <div
          className={`fp-overlay${sidebarOpen ? " active" : ""}`}
          onClick={() => setSidebarOpen(false)}
        />

        <aside className={`fp-sidebar${sidebarOpen ? " open" : ""}`}>
          <div className="fp-sidebar-header">
            <div className="fp-sidebar-header-left">
              <FontAwesomeIcon
                icon={faSlidersH}
                className="fp-sidebar-header-icon"
              />
              <span className="fp-sidebar-header-title">Filtros</span>
            </div>
            <div className="fp-sidebar-header-right">
              {hasActiveFilters && (
                <button className="fp-sidebar-clear-btn" onClick={clearAll}>
                  Limpiar
                </button>
              )}
              <button
                className="fp-sidebar-close-btn"
                onClick={() => setSidebarOpen(false)}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          </div>

          <div className="fp-sidebar-scroll">
            {hasActiveFilters && (
              <div className="fp-active-chips-bar">
                {selCat && (
                  <button
                    className="fp-chip"
                    onClick={() => updateParams({ cat: "", sub: "", tipo: "" })}
                  >
                    {selCat.name} <span className="fp-chip-x">×</span>
                  </button>
                )}
                {selSub && (
                  <button
                    className="fp-chip"
                    onClick={() => updateParams({ sub: "", cat: "", tipo: "" })}
                  >
                    {selSub.name} <span className="fp-chip-x">×</span>
                  </button>
                )}
                {[...selTipos].map((t) => (
                  <button
                    key={t}
                    className="fp-chip"
                    onClick={() => toggleTipo(t)}
                  >
                    {t} <span className="fp-chip-x">×</span>
                  </button>
                ))}
                {[...selBrands].map((id) => {
                  const b = allBrands.find((x) => x._id === id);
                  return b ? (
                    <button
                      key={id}
                      className="fp-chip"
                      onClick={() => toggleBrand(id)}
                    >
                      {b.name} <span className="fp-chip-x">×</span>
                    </button>
                  ) : null;
                })}
              </div>
            )}

            <FilterLabel title="Categorías">
              {categoryTree.map((cat) => {
                const isExpanded = expandedCatId === cat._id;
                const hasSubs = cat.subcategories?.length > 0;
                return (
                  <div key={cat._id}>
                    <div
                      className={`fp-cat-parent-label${isExpanded ? " open" : ""}${selCat?._id === cat._id ? " active" : ""}`}
                      onClick={() => hasSubs && toggleCat(cat)}
                      style={{ cursor: hasSubs ? "pointer" : "default" }}
                    >
                      <span>{cat.name}</span>
                      {hasSubs && (
                        <FontAwesomeIcon
                          icon={isExpanded ? faChevronUp : faChevronDown}
                          className="fp-cat-chevron"
                        />
                      )}
                    </div>
                    {isExpanded && hasSubs && (
                      <div className="fp-sub-list">
                        {cat.subcategories.map((sub) => {
                          const isSelSub = selSub?._id === sub._id;
                          const cnt = subCounts[sub._id];
                          return (
                            <div
                              key={sub._id}
                              className={`fp-sub-item${isSelSub ? " active" : ""}`}
                              onClick={() => selectSub(cat, sub)}
                            >
                              <span className="fp-sub-name">{sub.name}</span>
                              {cnt != null && (
                                <span className="fp-sub-count">{cnt}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </FilterLabel>

            {hasTipos && (
              <FilterSection title="Tipo">
                {availableTipos.map((t) => {
                  const isSel = selTipos.has(t.value);
                  return (
                    <div
                      key={t.value}
                      className={`fp-check-row${isSel ? " checked" : ""}`}
                      onClick={() => toggleTipo(t.value)}
                    >
                      <span
                        className={`fp-checkbox-box${isSel ? " checked" : ""}`}
                      >
                        <span className="fp-checkbox-tick" />
                      </span>
                      <span className="fp-check-name">{t.value}</span>
                      {t.count != null && (
                        <span className="fp-check-count">{t.count}</span>
                      )}
                    </div>
                  );
                })}
              </FilterSection>
            )}

            <FilterLabel title="Marcas">
              {allBrands
                .filter(
                  (b) => availableBrandIds.has(b._id) || selBrands.has(b._id),
                )
                .map((brand) => {
                  const isSel = selBrands.has(brand._id);
                  const cnt = brandCounts[brand._id];
                  return (
                    <div
                      key={brand._id}
                      className={`fp-check-row${isSel ? " checked" : ""}`}
                      onClick={() => toggleBrand(brand._id)}
                    >
                      <span
                        className={`fp-checkbox-box${isSel ? " checked" : ""}`}
                      >
                        <span className="fp-checkbox-tick" />
                      </span>
                      <span className="fp-check-name">{brand.name}</span>
                      {cnt != null && (
                        <span className="fp-check-count">{cnt}</span>
                      )}
                    </div>
                  );
                })}
            </FilterLabel>
          </div>
        </aside>

        <div className="fp-main">
          <div className="fp-mobile-bar">
            <button
              className="fp-filter-toggle"
              onClick={() => setSidebarOpen(true)}
            >
              <FontAwesomeIcon icon={faSlidersH} /> Filtros
              {activeCount > 0 && (
                <span className="fp-filter-badge">{activeCount}</span>
              )}
            </button>
          </div>

          <div className="fp-topbar">
            <div className="fp-topbar-left">
              <div className="fp-breadcrumb">
                <span className="fp-breadcrumb-link" onClick={clearAll}>
                  Todos
                </span>
                {selCat && (
                  <>
                    <span className="fp-breadcrumb-sep">›</span>
                    <span
                      className={
                        !selSub ? "fp-breadcrumb-current" : "fp-breadcrumb-link"
                      }
                      onClick={
                        !selSub
                          ? undefined
                          : () => updateParams({ sub: "", tipo: "" })
                      }
                    >
                      {selCat.name}
                    </span>
                  </>
                )}
                {selSub && (
                  <>
                    <span className="fp-breadcrumb-sep">›</span>
                    <span className="fp-breadcrumb-current">{selSub.name}</span>
                  </>
                )}
              </div>
            </div>
            <div className="fp-topbar-right">
              <div className="fp-search-box">
                <FontAwesomeIcon icon={faSearch} className="fp-search-icon" />
                <input
                  type="text"
                  className="fp-search-input"
                  placeholder="Buscar productos..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                {searchInput && (
                  <button
                    className="fp-search-clear"
                    onClick={() => {
                      setSearchInput("");
                      updateParams({ q: "" });
                    }}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                )}
              </div>
              <span className="sort-label">Ordenar por</span>
              <select
                className="sort-select"
                value={sortParam}
                onChange={(e) => updateParams({ sort: e.target.value })}
              >
                <option value="">Más relevantes</option>
                <option value="name_asc">Nombre A–Z</option>
                <option value="name_desc">Nombre Z–A</option>
              </select>
            </div>
          </div>

          <ProductsList
            categoryFilter={categoryFilter}
            brandFilter={brandFilter}
            tipoFilter={tipoFilter}
            searchTerm={searchTerm}
            page={page}
            onPageChange={setPage}
            sort={sortParam}
            onSortChange={(v) => updateParams({ sort: v })}
            selCat={selCat}
            selectedSubObjects={selectedSubObjects}
            onClearCat={clearAll}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
